import React, { useEffect, useState } from "react";
import { Card, CardBody } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { CardHeader } from "react-bootstrap";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { toast } from "react-toastify";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { CollectItem } from "../../CreateOrder/CollectItemsStep";
import { useGetCartSearchResultsMutation } from "../../../../redux/services/orderService";
import { useForm } from "react-hook-form";
import BreadCrumbs from "../../../../components/BreadCrumbs";
import ControlledSearch from "../../../../components/ControlledSearch";
import PlaceOrderButton from "../../../../components/LongButtonComponent";
import { CartItemRequest } from "../CartItemRequestView";
import { useLazyGetCartInventoryForRestaurantQuery } from "../../../../redux/services/restaurantServices";
import { useCreateCarttoCartOrderMutation, useLazyGetCartItemRequestByIdQuery } from "../../../../redux/services/cartToCartOrder";
import { InventoryId } from "../../../../utility/Utility";
import "./index.css";

type CreateOrderFormData = {
  paymentMethod: any;
  sourceCart: any;
  orderItems: any;
};

const CreateCartToCartOrder: React.FC = () => {
  const location = useLocation();
  const { state } = location;
  const cartRequest: CartItemRequest | undefined = state?.cartRequest;
  const navigate = useNavigate();
  const MySwal = withReactContent(Swal);

  const selectedRestaurantId = useSelector(
    (state: any) => state.restaurant.selectedRestaurantDetails.id
  );

  const selectedRestaurantData = useSelector(
    (state: any) => state.restaurant.selectedRestaurantDetails
  );

  // const [price, setPrice] = useState(0);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [orderItems, setOrderItems] = useState<CollectItem[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  // const [isCanCollectItems, setIsCanCollectItems] = useState<boolean>(false);
  const [cartOptions, setCartOptions] = useState<any>([]);
  const [cartLoading, setCartLoading] = useState(false);
  const [triggerCartSearchList] = useGetCartSearchResultsMutation();
  // lazy trigger to fetch cart inventory (destination + selected source)
  const [getCartInventory] = useLazyGetCartInventoryForRestaurantQuery();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [destinationInventory, setDestinationInventory] = useState<any | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [sourceInventory, setSourceInventory] = useState<any | null>(null);

  const [createCarttoCartOrderMutation, { isLoading }] =
    useCreateCarttoCartOrderMutation();

  const {
    control,
    handleSubmit,
    watch,
    setValue,
  } = useForm<CreateOrderFormData>({
    defaultValues: {
      paymentMethod: "",
      sourceCart: "",
      orderItems: "",
    },
  });

  const sourceCartWatcher = watch("sourceCart");

  useEffect(() => {
    if (!cartRequest) {
      toast.error("No cart item request data found");
      navigate(-1);
      return;
    }
    const cartId = cartRequest?.cart?.id;
    const inventoryId: string = InventoryId(new Date());
    if (cartId) {
      getCartInventory({ cartId, restaurantId: selectedRestaurantId, inventoryId })
        .then((res: any) => setDestinationInventory(res.data))
        .catch(() => {
          /* ignore errors for now */
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRestaurantId, cartRequest, navigate]);

  // useEffect(() => {
  //   if (sourceCartWatcher?.value) {
  //     setIsCanCollectItems(true);
  //   } else {
  //     setIsCanCollectItems(false);
  //   }
  // }, [sourceCartWatcher]);

  const orderPlaceSuccessPopup = (destinationOrder: any, sourceOrder: any) => {
    return MySwal.fire({
      title: "Order Placed Successfully..!",
      text: `References : ${sourceOrder?.orderReference} & ${destinationOrder?.orderReference}.`,
      icon: "success",
      showCancelButton: false,
      confirmButtonText: "Ok",
      customClass: {
        confirmButton: "btn btn-success",
      },
      buttonsStyling: false,
    }).then(() => {
      navigate("/orders/cart-item-request");
    });
  };

  const handleCartSearch = async (inputValue: string) => {
    setCartLoading(true);

    if (inputValue.length > 1) {
      try {
        const response = await triggerCartSearchList({ params: `query=${inputValue}` });

        if (response && "data" in response) {
          // Only include carts that are fixed-location in the source options
          let optionsList = (response.data?.hits || [])
            .filter((item: any) => item?.isFixedLocation === true)
            .map((item: any) => ({
              value: item.cartId,
              label: item.displayName,
            }));

          // Remove the destination cart (the one that requested items) so it cannot be selected as source
          const destinationCartId = cartRequest?.cart?.id;
          if (destinationCartId) {
            optionsList = optionsList.filter((opt: any) => opt.value !== destinationCartId);
          }

          setCartOptions(optionsList);
        }
      } catch (error) {
        toast.error("Error fetching cart results");
        console.error("Error fetching cart results:", error);
      }
    }
    setCartLoading(false);
  };

  const handleCartInputChange = (inputValue: string) => {
    if (inputValue.length > 2) {
      handleCartSearch(inputValue);
    }
  };

  const handleSourceCartSelect = (selectedOption: any) => {
    setValue("sourceCart", selectedOption);
  };

  const [transferQuantities, setTransferQuantities] = useState<Record<string, number>>({});

  useEffect(() => {
    // initialize transfer quantities from cartRequest items with 0 as default
    if (cartRequest?.orderItems) {
      const initial: Record<string, number> = {};
      cartRequest.orderItems.forEach((it) => {
        initial[it.menuItemId] = 0; // Start with 0 instead of requested quantity
      });
      setTransferQuantities(initial);
    }
  }, [cartRequest]);

  // Fetch full cart item request details (to get orderDetails) so pending counts match CartItemRequestView
  const [triggerGetCartItemRequest, { data: cartItemRequestData }] = useLazyGetCartItemRequestByIdQuery();

  useEffect(() => {
    if (cartRequest?.id) {
      triggerGetCartItemRequest(cartRequest.id);
    }
  }, [cartRequest?.id, triggerGetCartItemRequest]);

  const requestData = cartItemRequestData?.itemRequest?.request || cartRequest;
  const orderDetails = cartItemRequestData?.itemRequest?.orderDetails;

  // Calculate pending count for each item (same logic as CartItemRequestView)
  const getPendingCount = (item: any) => {
    if (!orderDetails || !requestData) return item.quantity || 0;
    let fulfilledQty = 0;
    for (const od of orderDetails) {
      if (od.orderStatus === "Cancelled") continue;
      const found = (od.orderItems || []).find((oi: any) => oi.menuItemId === item.menuItemId);
      if (found) fulfilledQty += found.quantity || 0;
    }
    return Math.max(0, item.quantity - fulfilledQty);
  };

  const handleTransferQtyChange = (menuItemId: string, value: number, max: number) => {
    const v = Math.max(0, Math.min(max, Number.isNaN(value) ? 0 : value));
    setTransferQuantities((prev) => ({ ...prev, [menuItemId]: v }));
  };

  // helper: compute available inventory for a menu item from destinationInventory
  const getDestinationInventoryCount = (item: any) => {
    // destinationInventory is stored in state as `destinationInventory`
    if (!destinationInventory?.items || !item?.menuItemId) return 0;
    const portionSize = item.portionSize || "REGULAR";
    const itemInventory = destinationInventory.items[item.menuItemId]?.[portionSize];
    if (!itemInventory) return 0;
    const {
      totalReceived = 0,
      totalReserved = 0,
      directIssues = 0,
      totalReturned = 0,
      cartTransferCount = 0,
    } = itemInventory;
    // Inventory = totalReceived - [totalReserved + directIssues + totalReturned + cartTransferCount]
    return Math.max(
      0,
      totalReceived - (totalReserved + directIssues + totalReturned + cartTransferCount)
    );
  };
  const getSourceInventoryCount = (item: any) => {
    if (!sourceInventory?.items || !item?.menuItemId) return 0;
    const portionSize = item.portionSize || "REGULAR";
    const itemInventory = sourceInventory.items[item.menuItemId]?.[portionSize];
    if (!itemInventory) return 0;
    const {
      totalReceived = 0,
      totalReserved = 0,
      directIssues = 0,
      totalReturned = 0,
      cartTransferCount = 0,
    } = itemInventory;
    return Math.max(
      0,
      totalReceived - (totalReserved + directIssues + totalReturned + cartTransferCount)
    );
  };

  // Derived flag: enable create button only when at least one transfer qty > 0
  const hasAnyTransfer = (cartRequest?.orderItems || []).some(
    (it) => (transferQuantities[it.menuItemId] ?? 0) > 0
  );

  // Check if source cart has any stock available
  const sourceCartHasStock = sourceCartWatcher?.value && (cartRequest?.orderItems || []).some(
    (it) => getSourceInventoryCount(it) > 0
  );

  const disableCreateButton = isLoading || !sourceCartWatcher?.value || !hasAnyTransfer || !sourceCartHasStock;

  const onSubmit = async (data: any) => {
    if (!cartRequest) {
      toast.error("No cart item request data found");
      return;
    }
    const sendProductList = Array.isArray(orderItems)
      ? cartRequest?.orderItems.map((item: any) => ({
        unitPrice: item.unitPrice,
        itemType: item.itemType === "Single" ? "MenuItem" : item.itemType,
        displayName: item.displayName,
        portionSize: item.portionSize,
        menuItemId: item.menuItemId,
        quantity: item.quantity,
      }))
      : [];

    const sourceOrderItems = sendProductList.map((p) => ({
      ...p,
    }));

    // First order - normal cart order (destination)
    const sourceOrderObj = {
      address: selectedRestaurantData?.address,
      cartId: sourceCartWatcher?.value,
      displayName: selectedRestaurantData?.displayName,
      imageUrl: selectedRestaurantData.imageUrl,
      orderCreatedBy: "Restaurant",
      paymentMethod: "Platform",
      restaurantId: selectedRestaurantId,
      customerId: null,
      isTentativeOrder: false,
      isScheduledOrder: false,
      isDeliveryOrder: false,
      totalPrice: 0,
      orderItems: sendProductList,
      isDirectRestaurantOrder: false,
      cartItemRequestId: cartRequest.id,
      orderStatus: "ApprovedByRestaurant",
      orderSource: "CallCenter" as const,
      isAdminItemRequest: true,
    };

    // Second order - source cart order with admin request properties
    const destinationOrderObj = {
      address: selectedRestaurantData?.address,
      cartId: cartRequest.cart?.id,
      displayName: selectedRestaurantData?.displayName,
      imageUrl: selectedRestaurantData.imageUrl,
      orderCreatedBy: "Restaurant",
      paymentMethod: "Platform",
      restaurantId: selectedRestaurantId,
      customerId: null,
      isTentativeOrder: false,
      isScheduledOrder: false,
      isDeliveryOrder: false,
      totalPrice: 0,
      orderItems: sourceOrderItems,
      isDirectRestaurantOrder: false,
      cartItemRequestId: cartRequest.id,
      orderStatus: "ApprovedByRestaurant",
      orderSource: "CallCenter" as const,
      isAdminItemRequest: false,
    };

    try {
      const [destinationResponse] = await Promise.all([
        createCarttoCartOrderMutation({ sourceOrder: sourceOrderObj, destinationOrder: destinationOrderObj }).unwrap(),
      ]);
      orderPlaceSuccessPopup(destinationResponse?.destinationOrder, destinationResponse?.sourceOrder);

    } catch (error: any) {
      console.error("Order creation failed:", error);
      toast.error(`Something went wrong. Please try again.`);
    }
  };

  useEffect(() => {
    const srcCartId = sourceCartWatcher?.value;
    if (srcCartId) {
      const inventoryId = InventoryId(new Date());
      getCartInventory({ cartId: srcCartId, restaurantId: selectedRestaurantId, inventoryId })
        .then((res: any) => setSourceInventory(res.data))
        .catch(() => {
          /* ignore errors for now */
        });
    } else {
      setSourceInventory(null);
    }
  }, [sourceCartWatcher?.value, getCartInventory, selectedRestaurantId]);

  if (!cartRequest) {
    return (
      <div className="alert alert-danger">
        No cart item request data found. Please go back and try again.
      </div>
    );
  }

  return (
    <>
      <BreadCrumbs hasSubMenus={true} />
      <Card className="mb-3">
        <CardHeader className="d-flex align-items-start justify-content-between bg-white">
          <div>
            <h3>Transfer Order Creation & Request Management</h3>
            <small className="text-muted">Fulfill requests from {cartRequest?.cart?.displayName || "source cart"}.</small>
          </div>
        </CardHeader>
        <CardBody>
          <div className="d-flex justify-content-between align-items-center">
            <div className="section-subtitle">Request from: {cartRequest?.cart?.displayName || "—"}</div>
            <div className="source-search-wrapper">
              <ControlledSearch
                key={`source-cart-search-top`}
                label=""
                inputName={"sourceCart"}
                control={control}
                optionsList={cartOptions}
                loading={cartLoading}
                handleInputChange={handleCartInputChange}
                onChange={handleSourceCartSelect}
                isRequired={true}
                placeholder={"Search For Source Cart..."}
              />
            </div>
          </div>
          <div className="cart-transfer-grid-gap">
            <div className="cart-transfer-section-subtitle">Fulfill Requested Items</div>
            {(cartRequest?.orderItems || []).map((it, idx) => {
              // replace demo inStock value with real destination inventory
              const inStock = getDestinationInventoryCount(it);
              const sourceCartInStock = getSourceInventoryCount(it);
              const pending = getPendingCount(it);
              const currentTransfer = transferQuantities[it.menuItemId] ?? 0;
              return (
                <div key={it.menuItemId} className="cart-transfer-requested-item-row p-2 border rounded d-flex align-items-center justify-content-between">
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <div className="cart-transfer-dot-available" />
                    <div>
                      <div className="cart-transfer-fw-600">{it.displayName}</div>
                      <div className="cart-transfer-small-text cart-transfer-text-muted">
                        {it.itemType === "Gold" && <span className="badge bg-warning text-dark me-1">Gold</span>}
                        {it.itemType === "Silver" && <span className="badge bg-secondary me-1">Silver</span>}
                        {/* add other badges if needed */}
                      </div>
                    </div>
                  </div>
                  <div className="d-flex align-items-center cart-transfer-gap-16">
                    <div className="cart-transfer-text-right cart-transfer-small-text">
                      <div className="cart-transfer-text-muted">Pending</div>
                      <div className="cart-transfer-fw-700">{pending}</div>
                    </div>
                    <div className="cart-transfer-text-right cart-transfer-small-text">
                      <div className="cart-transfer-text-muted">Destination Cart In Stock</div>
                      <div className="cart-transfer-fw-700">{inStock}</div>
                    </div>
                    {sourceCartWatcher?.value ? (
                      <div className="cart-transfer-text-right cart-transfer-small-text">
                        <div className="cart-transfer-text-muted">Soruce Cart In Stock</div>
                        <div className="cart-transfer-fw-700">{sourceCartInStock}</div>
                      </div>
                    ) : ("")}
                    <div className="cart-transfer-transfer-qty">
                      <label className="cart-transfer-block-12 cart-transfer-text-muted">Transfer Qty</label>
                      <input
                        type="number"
                        className="form-control"
                        min={0}
                        max={sourceCartInStock}
                        value={currentTransfer}
                        defaultValue={0}
                        disabled={!sourceCartWatcher?.value || sourceCartInStock === 0}
                        onChange={(e) => handleTransferQtyChange(it.menuItemId, Number(e.target.value), sourceCartInStock)}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="d-flex justify-content-end mt-3">
            <PlaceOrderButton
              title={isLoading ? "Creating Order..." : "+ Create Transfer Order"}
              isDisabled={disableCreateButton}
              onClick={handleSubmit(onSubmit)}
              icon={
                isLoading ? (
                  <FontAwesomeIcon icon={faSpinner} spin />
                ) : null
              }
            />
          </div>
        </CardBody>
      </Card>
      {/* <Row className="mt-0">
        <Col lg={6}>
          <Card className="card">
            <CardHeader className="">
              <h5 className="header-text">Cart Item Request Order</h5>
            </CardHeader>
            <CardBody>
              <div className="mb-4">
                <Row>
                  <Col lg="4">
                    <Label className="fw-bold">Destination Cart</Label>
                  </Col>
                  <Col>
                    <Input
                      type="text"
                      value={cartRequest.cart?.displayName || "No Cart Assigned"}
                      disabled
                      className="form-control"
                    />

                  </Col>
                </Row>
              </div>

              <div className="mb-3">
                <ControlledSearch
                  key={`source-cart-search-${searchKey}`}
                  label="Source Cart"
                  inputName={"sourceCart"}
                  control={control}
                  optionsList={cartOptions}
                  loading={cartLoading}
                  handleInputChange={handleCartInputChange}
                  onChange={handleSourceCartSelect}
                  isRequired={true}
                  placeholder={"Search for source cart..."}
                />

              </div>

              <div className="mb-4">
                <ControlledSelect
                  label={"Payment Method"}
                  inputName={"paymentMethod"}
                  control={control}
                  isMulti={false}
                  optionsList={PaymentMethod}
                  rules={{ required: true }}
                  isRequired={true}
                />
              </div>
            </CardBody>
          </Card>
        </Col>
        <Col lg={6}>
          <Row className="place-order-button mt-1">
            <PlaceOrderButton
              titel={
                isPaymentLoading
                  ? "Calculating the final price"
                  : "Place Order"
              }
              isDisabled={isLoading || orderItems.length === 0 || isPaymentLoading || !sourceCartWatcher?.value}
              onClick={handleSubmit(onSubmit)}
              icon={
                isLoading || isPaymentLoading ? (
                  <FontAwesomeIcon icon={faSpinner} spin />
                ) : null
              }
            />
          </Row>
          <OrderSummaryStep
            orderItems={orderItems}
            setOrderItems={setOrderItems}
            cartPersonWatcher={sourceCartWatcher}
            orderByWatcher={{ value: "CartPerson" }}
            emailWatcher={null}
            isLoading={isLoading}
            phoneNumberWatcher={null}
            setPrice={setPrice}
            price={paymentDetails?.netAmount || price}
            paymentDetails={paymentDetails}
            isTentativeOrder={false}
            isDeliveryOrder={false}
          />
        </Col>
      </Row>
      <Card className="card">
        <CardHeader className="card-header">
          <Row>
            <Col>
              <h5 className="header-text mt-2">Select Items to Transfer</h5>
            </Col>
            <Col lg="4" className="mt-2">
              <Input
                value={searchValue}
                onChange={handleFilter}
                placeholder="Search items ..."
              />
            </Col>
          </Row>
          <hr />
        </CardHeader>
        <CardBody className="card-body">
          <CollectItemsStep
            watch={watch}
            setOrderItems={setOrderItems}
            orderItems={orderItems}
            selectedMenu={selectedMenu}
            setSelectedMenu={setSelectedMenu}
            setSelectedMenuItemList={setSelectedMenuItemList}
            menus={menus}
            selectedMenuItemList={
              filteredData.length > 0 ? filteredData : selectedMenuItemList
            }
            setFilteredData={filteredData}
            singleMenuItemsList={singleMenuItemsList}
            comboItemList={comboItemList}
            customMenuItemList={customMenuItemList}
            setSingleMenuItemsList={setSingleMenuItemsList}
            setComboItemList={setComboItemList}
            setCustomMenuItemList={setCustomMenuItemList}
            control={control}
            sendCustomObj={sendCustomObj}
            setSendCustomObj={setSendCustomObj}
            orderByWatcher={{ value: "CartPerson" }}
            isCanCollectItems={isCanCollectItems}
            scheduledOrderDetails={scheduledOrderDetails}
            isInitialLoading={isInitialLoading}
            isTentativeOrder={false}
          />
        </CardBody>
      </Card>
      <CollectedItemsTable
        orderItems={orderItems}
        setOrderItems={setOrderItems}
      /> */}
    </>
  );
};

export default CreateCartToCartOrder;